// Fixed marine wind-speed scale: pure math, no React/DOM. Ported from the
// Marine Forecast Rail design handoff (docs/design_handoff_marine_forecast/)
// so a wind speed always maps to the same track position and severity color
// everywhere it's drawn (chart fill, gust hatch, axis-label numbers).
//
// Anchors sit at standard marine thresholds so a hurricane fills the bar:
// 0 kt calm, 11 kt fresh breeze, 22 kt small-craft advisory, 34 kt gale,
// 48 kt storm, 64+ kt hurricane force (saturates at 100%).
const SCALE_ANCHORS = [
  [0, 0],
  [11, 20],
  [22, 40],
  [34, 60],
  [48, 80],
  [64, 100],
]

// kt -> 0..100% track position (piecewise-linear across SCALE_ANCHORS).
export function scalePos(kt) {
  if (kt == null || !isFinite(kt) || kt <= 0) return 0
  if (kt >= 64) return 100
  for (let i = 1; i < SCALE_ANCHORS.length; i++) {
    const [v0, p0] = SCALE_ANCHORS[i - 1]
    const [v1, p1] = SCALE_ANCHORS[i]
    if (kt <= v1) return p0 + ((kt - v0) / (v1 - v0)) * (p1 - p0)
  }
  return 100
}

// Severity ramp sampled at the scale position, teal -> amber -> orange -> red
// -> crimson, so a number's color always matches the fill/hatch beneath it.
const SEVERITY_STOPS = [
  [0, [53, 176, 192]],
  [20, [53, 176, 192]],
  [40, [224, 179, 65]],
  [60, [224, 130, 54]],
  [80, [209, 82, 74]],
  [100, [161, 29, 58]],
]

// kt -> [r,g,b] at that speed's scale position.
export function sampleRGB(kt) {
  if (kt == null || !isFinite(kt)) return [95, 149, 165]
  const p = scalePos(kt)
  for (let i = 1; i < SEVERITY_STOPS.length; i++) {
    const [p0, c0] = SEVERITY_STOPS[i - 1]
    const [p1, c1] = SEVERITY_STOPS[i]
    if (p <= p1) {
      const t = p1 === p0 ? 0 : (p - p0) / (p1 - p0)
      return c0.map((v, j) => Math.round(v + (c1[j] - v) * t))
    }
  }
  return [161, 29, 58]
}

export function sampleScale(kt) {
  return 'rgb(' + sampleRGB(kt).join(',') + ')'
}

// 45deg repeating stripe, tinted by the gust speed's severity color, for the
// gust-hatch region of the wind chart (sustained -> gust position).
export function gustHatch(gustKt) {
  const [r, g, b] = sampleRGB(gustKt)
  return `repeating-linear-gradient(45deg,rgba(${r},${g},${b},.85) 0 3px,rgba(${r},${g},${b},.32) 3px 6px)`
}

export const WIND_SEVERITY_GRADIENT =
  'linear-gradient(90deg,#35b0c0 0%,#35b0c0 20%,#e0b341 40%,#e08236 60%,#d1524a 80%,#a11d3a 100%)'
